import { z } from 'zod';
import type { StatutCandidature } from './candidature.js';

/** Défaut du paramètre `vivier.note.minimum` : note ≥ 3 ou pastille ≥ 3. */
export const NOTE_MINIMUM_VIVIER = 3;
/**
 * Source d'une candidature rattachée depuis le vivier. Ce n'est pas une source
 * de message (`SOURCES`) : l'origine se lit dans `issue_de_candidature_id`.
 */
export const SOURCE_VIVIER = 'vivier';
/** Au-delà, la liste des anciens candidats n'est plus lisible sur un téléphone. */
export const MAX_ANNONCES_VIVIER = 50;

/**
 * Une ancienne annonce proposée dans le panneau « Inclure les anciennes
 * candidatures » (docs/UI.md § Écran 1).
 */
export interface AnnonceVivier {
  id: string;
  reference: string;
  intitule: string;
  famille: string;
  familleCode: string;
  /** Même famille que l'annonce courante : listée d'abord, hors du bloc « autres familles ». */
  memeFamille: boolean;
  statut: string;
  publieeLe: string | null;
  closeLe: string | null;
  candidats: number;
  evalues: number;
  /** Dernière version validée, pour lire le texte d'époque ; absente si jamais saisie. */
  version: number | null;
}

export interface ChoixVivier {
  annonces: AnnonceVivier[];
  /** Sélection mémorisée de l'utilisateur pour cette annonce. */
  selection: string[];
  qualite: boolean;
  noteMinimum: number;
}

export const selectionVivierSchema = z
  .object({
    annonces: z.array(z.string().uuid()).max(MAX_ANNONCES_VIVIER),
    qualite: z.boolean(),
  })
  .strict();
export type SelectionVivier = z.infer<typeof selectionVivierSchema>;

/** POST /candidatures/:id/rattacher */
export const rattachementVivierSchema = z.object({ annonceCibleId: z.string().uuid() }).strict();
export type RattachementVivier = z.infer<typeof rattachementVivierSchema>;

/** PUT /personnes/:id/ne-plus-contacter */
export const nePlusContacterSchema = z
  .object({
    nePlusContacter: z.boolean(),
    motif: z.string().trim().max(500).nullable().optional(),
  })
  .strict();
export type NePlusContacter = z.infer<typeof nePlusContacterSchema>;

/** Candidature d'origine d'un rattachement : la note se consulte, elle ne se transporte pas. */
export interface OrigineVivier {
  candidatureId: string;
  annonceReference: string;
  annonceIntitule: string;
  statut: StatutCandidature;
  recueLe: string;
  potentiel: number | null;
  potentielLe: string | null;
  noteEntretien: number | null;
  noteLe: string | null;
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Africa/Tunis' });
}

/**
 * Mois entiers écoulés depuis la dernière nouvelle du candidat : sa
 * candidature, ou la dernière mise à jour de son profil.
 */
export function moisSansMiseAJour(
  c: { recueLe: string; profilModifieLe: string | null },
  maintenant: Date = new Date(),
): number {
  const derniere = new Date(
    Math.max(new Date(c.recueLe).getTime(), c.profilModifieLe ? new Date(c.profilModifieLe).getTime() : 0),
  );
  let mois =
    (maintenant.getFullYear() - derniere.getFullYear()) * 12 + (maintenant.getMonth() - derniere.getMonth());
  if (maintenant.getDate() < derniere.getDate()) mois -= 1;
  return Math.max(0, mois);
}

export function mentionMiseAJour(mois: number): string {
  if (mois < 1) return 'profil mis à jour il y a moins d’un mois';
  return `profil non mis à jour depuis ${mois} mois`;
}

/** Rappel affiché sur une candidature rattachée : « déjà évalué N le … ». */
export function rappelOrigine(origine: OrigineVivier): string {
  if (origine.noteEntretien !== null && origine.noteLe) {
    const note = origine.noteEntretien.toLocaleString('fr-FR');
    return `Déjà évalué ${note} le ${jour(origine.noteLe)} sur ${origine.annonceReference}`;
  }
  if (origine.potentiel !== null && origine.potentielLe) {
    return `Pastille ${origine.potentiel} le ${jour(origine.potentielLe)} sur ${origine.annonceReference}, sans entretien noté`;
  }
  return `Candidature du ${jour(origine.recueLe)} sur ${origine.annonceReference}, jamais évaluée`;
}

/** « 13/09/2026 – en cours » ou « 02/03/2026 – 30/04/2026 ». */
export function periodeAnnonce(a: Pick<AnnonceVivier, 'publieeLe' | 'closeLe'>): string {
  const debut = a.publieeLe ? jour(a.publieeLe) : 'non publiée';
  return `${debut} – ${a.closeLe ? jour(a.closeLe) : 'en cours'}`;
}
